import Button, {ButtonSize, ButtonVariant} from "@components/Button";
import Icon from "@components/Icon";
import {buildBookingLink, parseBookingQuery} from "@page-containers/booking/utils/bookingQuery";
import {guess} from "@services/api";
import {cancelAppointment} from "@services/monolith/appointments";
import moment from "moment-timezone";
import {useRouter} from "next/router";
import {useTranslation} from "@i18n/client";
import {PropsWithChildren, memo, useCallback, useState} from "react";

import {MonolithLocationFields} from "../../../../_services/graphql/queries/location";
import useTodayTomorrowString from "../../../../hooks/booking/useTodayTomorrowString";
import {useDisclosure} from "../../../../hooks/useDisclosure";
import {I18nNamespace} from "@i18n";

type CancelScreenContentProps = {
  ariaDescriptionId: string;
  location: MonolithLocationFields | null;
  selectedTime: number | null;
};
const CancelScreenContent = ({
  ariaDescriptionId,
  location,
  selectedTime,
}: CancelScreenContentProps) => {
  const timezone = location?.timezone ?? guess();
  const apptTime = moment(selectedTime).tz(timezone);
  const {t} = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const router = useRouter();
  const {canceled} = parseBookingQuery(router.query);
  const [error, setError] = useState("");
  const dateString = useTodayTomorrowString(apptTime, moment().tz(timezone));
  const {open: onCancelStart, close: onCancelError, isOpen: isCanceling} = useDisclosure(false);
  const onError = useCallback(() => {
    onCancelError();
    setError(
      t("An internal error occurred. Please try again in a few minutes.", {
        ns: "web-scheduling",
      }),
    );
  }, [onCancelError, setError, t]);
  const locationTextStyles = canceled ? "text-center line-through" : "text-center";
  return (
    <div className="flex flex-col items-center gap-8 sm:gap-10">
      <div className="flex flex-col gap-4 items-center">
        {canceled ? (
          <Icon
            className="bg-green-200 rounded-full h-16 w-16 leading-[64px] text-2xl text-center"
            icon="tick"
          />
        ) : null}
        {!canceled ? (
          <Icon
            className="bg-yellow-100 rounded-full h-16 w-16 leading-[64px] text-[2rem] text-center"
            icon="calendar-with-do-not-enter"
          />
        ) : null}
        <h1 id={ariaDescriptionId} className="text-center">
          {canceled
            ? t("Your visit has been canceled", {ns: "web-scheduling"})
            : t("Cancel your booked appointment?", {ns: "web-scheduling"})}
        </h1>
        <div className="flex flex-col">
          <p className={locationTextStyles}>{dateString}</p>
          <p className={locationTextStyles}>{location?.address?.firstLine}</p>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row justify-center gap-2 w-full">
        {canceled ? (
          <>
            <BookNewButton />
            <OkayButton />
          </>
        ) : (
          <>
            <RescheduleButton disabled={isCanceling} />
            <CancelButton disabled={isCanceling} onCancelStart={onCancelStart} onError={onError} />
          </>
        )}
        {error ? <p className="text-error-700 text-center empty:hidden">{error}</p> : null}
      </div>
    </div>
  );
};

type RescheduleButtonProps = {
  disabled: boolean;
};
const RescheduleButton = ({disabled}: RescheduleButtonProps) => {
  const router = useRouter();
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const onClick = useCallback(() => {
    const parsedQuery = parseBookingQuery(router.query);
    router.replace(
      buildBookingLink({
        ...parsedQuery,
        selectedTime: undefined,
        selectedSlotIds: undefined,
        canceling: undefined,
        rescheduling: true,
      }),
      undefined,
      {shallow: true},
    );
  }, [router]);
  return (
    <CancelScreenButton disabled={disabled} variant={ButtonVariant.GHOST} onClick={onClick}>
      {i18n.t("Reschedule", {ns: "web-scheduling"})}
    </CancelScreenButton>
  );
};

type CancelButtonProps = {
  disabled: boolean;
  onCancelStart: () => void;
  onError: () => void;
};
const CancelButton = ({disabled, onCancelStart, onError}: CancelButtonProps) => {
  const router = useRouter();
  const {appointmentId} = parseBookingQuery(router.query);
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const onClick = useCallback(async () => {
    if (appointmentId) {
      onCancelStart();
      try {
        await cancelAppointment(appointmentId);
        router.replace(
          buildBookingLink({
            ...parseBookingQuery(router.query),
            canceling: null,
            canceled: true,
          }),
        );
      } catch (e) {
        onError();
      }
    }
  }, [appointmentId, onCancelStart, onError, router]);
  return (
    <CancelScreenButton disabled={disabled} variant={ButtonVariant.DESTROY} onClick={onClick}>
      {i18n.t("Cancel Appointment", {ns: "web-scheduling"})}
    </CancelScreenButton>
  );
};

const BookNewButton = () => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const router = useRouter();
  const {appointmentReasonSlug, locationSlug, specialtyId} = parseBookingQuery(router.query);
  const onClick = useCallback(() => {
    router.push(
      buildBookingLink({
        appointmentReasonSlug,
        locationSlug,
        specialtyId,
        selectedPatientId: null,
      }),
      undefined,
      {shallow: true},
    );
  }, [appointmentReasonSlug, locationSlug, router, specialtyId]);

  return (
    <CancelScreenButton variant={ButtonVariant.GHOST} onClick={onClick}>
      {i18n.t("Book A New Appointment", {ns: "web-scheduling"})}
    </CancelScreenButton>
  );
};

const OkayButton = () => {
  const i18n = useTranslation(I18nNamespace.WEB_SCHEDULING);
  const router = useRouter();
  const {appointmentReasonSlug, originUrl} = parseBookingQuery(router.query);
  const closeUrl = originUrl || `/get-care/${appointmentReasonSlug}`;

  return (
    <CancelScreenButton href={closeUrl}>
      {i18n.t("Okay", {ns: "web-scheduling"})}
    </CancelScreenButton>
  );
};

type CancelScreenButtonProps = {
  disabled?: boolean;
  variant?: ButtonVariant;
  onClick?: () => void;
  href?: string;
};
const CancelScreenButton = memo(
  ({children, disabled, href, variant, onClick}: PropsWithChildren<CancelScreenButtonProps>) => {
    return (
      <Button
        className="w-full sm:max-w-[18.5rem]"
        disabled={disabled}
        variant={variant}
        size={ButtonSize.LG}
        href={href}
        onClick={onClick}
      >
        {children}
      </Button>
    );
  },
);
CancelScreenButton.displayName = "CancelScreenButton";

export default CancelScreenContent;
